import {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import {
  type DockItem,
  type DockState,
  type DockContextValue,
  type DockActionType,
  DEFAULT_DOCK_ITEMS,
  MAX_VISIBLE_ITEMS,
} from '../../types/dock';

/**
 * Action types for the dock reducer
 */
type DockReducerAction =
  | { type: 'ADD_ITEM'; payload: Omit<DockItem, 'position'> }
  | { type: 'REMOVE_ITEM'; payload: { itemId: string } }
  | { type: 'REORDER_ITEMS'; payload: { itemIds: string[] } }
  | { type: 'MOVE_ITEM'; payload: { itemId: string; newPosition: number } }
  | { type: 'TOGGLE_ITEM_VISIBILITY'; payload: { itemId: string } }
  | { type: 'OPEN_DRAWER' }
  | { type: 'CLOSE_DRAWER' }
  | { type: 'TOGGLE_DRAWER' }
  | { type: 'ENABLE_EDIT_MODE' }
  | { type: 'DISABLE_EDIT_MODE' }
  | { type: 'TOGGLE_EDIT_MODE' }
  | { type: 'SET_DOCK_HOVER'; payload: boolean }
  | { type: 'SET_DOCK_DIMENSIONS'; payload: { width: number; height: number } };

/**
 * Initial state for the dock
 */
const initialState: DockState = {
  items: DEFAULT_DOCK_ITEMS,
  isDrawerOpen: false,
  isEditing: false,
  isDockHovered: false,
  dockDimensions: { width: 0, height: 0 },
};

/**
 * Recalculates positions for all items based on their order in the array
 */
function recalculatePositions(items: DockItem[]): DockItem[] {
  return items.map((item, index) => ({
    ...item,
    position: index,
  }));
}

/**
 * Updates item visibility based on position and MAX_VISIBLE_ITEMS
 */
function updateVisibility(items: DockItem[]): DockItem[] {
  return items.map((item, index) => ({
    ...item,
    isVisible: index < MAX_VISIBLE_ITEMS,
  }));
}

/**
 * Reducer function for dock state management
 */
function dockReducer(state: DockState, action: DockReducerAction): DockState {
  switch (action.type) {
    case 'ADD_ITEM': {
      const newItem: DockItem = {
        ...action.payload,
        position: state.items.length,
      };
      const updatedItems = updateVisibility([...state.items, newItem]);
      return { ...state, items: updatedItems };
    }

    case 'REMOVE_ITEM': {
      const itemToRemove = state.items.find(
        (item) => item.id === action.payload.itemId
      );
      // Prevent removal of system items
      if (!itemToRemove || itemToRemove.type === 'system') {
        return state;
      }
      const filteredItems = state.items.filter(
        (item) => item.id !== action.payload.itemId
      );
      const updatedItems = updateVisibility(recalculatePositions(filteredItems));
      return { ...state, items: updatedItems };
    }

    case 'REORDER_ITEMS': {
      const { itemIds } = action.payload;
      const itemMap = new Map(state.items.map((item) => [item.id, item]));
      const reorderedItems: DockItem[] = [];

      // Add items in the new order
      for (const id of itemIds) {
        const item = itemMap.get(id);
        if (item) {
          reorderedItems.push(item);
          itemMap.delete(id);
        }
      }

      // Add any remaining items that weren't in the reorder list
      for (const item of itemMap.values()) {
        reorderedItems.push(item);
      }

      const updatedItems = updateVisibility(recalculatePositions(reorderedItems));
      return { ...state, items: updatedItems };
    }

    case 'MOVE_ITEM': {
      const { itemId, newPosition } = action.payload;
      const currentIndex = state.items.findIndex((item) => item.id === itemId);

      if (currentIndex === -1) {
        return state;
      }

      const items = [...state.items];
      const [movedItem] = items.splice(currentIndex, 1);
      const targetPosition = Math.max(0, Math.min(newPosition, items.length));
      items.splice(targetPosition, 0, movedItem);

      const updatedItems = updateVisibility(recalculatePositions(items));
      return { ...state, items: updatedItems };
    }

    case 'TOGGLE_ITEM_VISIBILITY': {
      const { itemId } = action.payload;
      const itemIndex = state.items.findIndex((item) => item.id === itemId);

      if (itemIndex === -1) {
        return state;
      }

      const item = state.items[itemIndex];
      const items = [...state.items];

      if (item.isVisible) {
        // Move to drawer (end of list)
        const [movedItem] = items.splice(itemIndex, 1);
        items.push(movedItem);
      } else {
        // Move to visible area (before position MAX_VISIBLE_ITEMS)
        const [movedItem] = items.splice(itemIndex, 1);
        const insertPosition = Math.min(MAX_VISIBLE_ITEMS - 1, items.length);
        items.splice(insertPosition, 0, movedItem);
      }

      const updatedItems = updateVisibility(recalculatePositions(items));
      return { ...state, items: updatedItems };
    }

    case 'OPEN_DRAWER':
      return { ...state, isDrawerOpen: true };

    case 'CLOSE_DRAWER':
      return { ...state, isDrawerOpen: false, isEditing: false };

    case 'TOGGLE_DRAWER':
      return {
        ...state,
        isDrawerOpen: !state.isDrawerOpen,
        isEditing: state.isDrawerOpen ? false : state.isEditing,
      };

    case 'ENABLE_EDIT_MODE':
      return { ...state, isEditing: true };

    case 'DISABLE_EDIT_MODE':
      return { ...state, isEditing: false };

    case 'TOGGLE_EDIT_MODE':
      return { ...state, isEditing: !state.isEditing };

    case 'SET_DOCK_HOVER':
      return { ...state, isDockHovered: action.payload };

    case 'SET_DOCK_DIMENSIONS':
      return { ...state, dockDimensions: action.payload };

    default:
      return state;
  }
}

/**
 * Context for dock state and actions
 */
const DockContext = createContext<DockContextValue | null>(null);

/**
 * Props for DockProvider component
 */
interface DockProviderProps {
  children: ReactNode;
  /** Optional initial items to override defaults */
  initialItems?: DockItem[];
}

/**
 * Provider component for dock state management
 */
export function DockProvider({
  children,
  initialItems,
}: DockProviderProps): JSX.Element {
  const [state, dispatch] = useReducer(dockReducer, {
    ...initialState,
    items: initialItems ?? initialState.items,
  });

  const addItem = useCallback((item: Omit<DockItem, 'position'>) => {
    dispatch({ type: 'ADD_ITEM', payload: item });
  }, []);

  const removeItem = useCallback((itemId: string) => {
    dispatch({ type: 'REMOVE_ITEM', payload: { itemId } });
  }, []);

  const reorderItems = useCallback((itemIds: string[]) => {
    dispatch({ type: 'REORDER_ITEMS', payload: { itemIds } });
  }, []);

  const moveItem = useCallback((itemId: string, newPosition: number) => {
    dispatch({ type: 'MOVE_ITEM', payload: { itemId, newPosition } });
  }, []);

  const toggleItemVisibility = useCallback((itemId: string) => {
    dispatch({ type: 'TOGGLE_ITEM_VISIBILITY', payload: { itemId } });
  }, []);

  const openDrawer = useCallback(() => {
    dispatch({ type: 'OPEN_DRAWER' });
  }, []);

  const closeDrawer = useCallback(() => {
    dispatch({ type: 'CLOSE_DRAWER' });
  }, []);

  const toggleDrawer = useCallback(() => {
    dispatch({ type: 'TOGGLE_DRAWER' });
  }, []);

  const enableEditMode = useCallback(() => {
    dispatch({ type: 'ENABLE_EDIT_MODE' });
  }, []);

  const disableEditMode = useCallback(() => {
    dispatch({ type: 'DISABLE_EDIT_MODE' });
  }, []);

  const toggleEditMode = useCallback(() => {
    dispatch({ type: 'TOGGLE_EDIT_MODE' });
  }, []);

  const setDockHover = useCallback((isHovered: boolean) => {
    dispatch({ type: 'SET_DOCK_HOVER', payload: isHovered });
  }, []);

  const setDockDimensions = useCallback((width: number, height: number) => {
    dispatch({ type: 'SET_DOCK_DIMENSIONS', payload: { width, height } });
  }, []);

  const triggerAction = useCallback((action: DockActionType) => {
    // Parse and handle the action
    const [actionType, actionTarget] = action.split(':') as [string, string];

    switch (actionType) {
      case 'modal':
        // TODO: Integrate with modal system when implemented
        // Modal actions will be handled by a future modal context
        void actionTarget;
        break;
      case 'route':
        // TODO: Integrate with router when routes are defined
        // Route actions will be handled by React Router
        void actionTarget;
        break;
      case 'external':
        // Open external URL in new tab
        window.open(actionTarget, '_blank', 'noopener,noreferrer');
        break;
      default:
        // Unknown action types are silently ignored
        // This allows for future extensibility
        void actionType;
    }
  }, []);

  const contextValue = useMemo<DockContextValue>(
    () => ({
      ...state,
      addItem,
      removeItem,
      reorderItems,
      moveItem,
      toggleItemVisibility,
      openDrawer,
      closeDrawer,
      toggleDrawer,
      enableEditMode,
      disableEditMode,
      toggleEditMode,
      setDockHover,
      setDockDimensions,
      triggerAction,
    }),
    [
      state,
      addItem,
      removeItem,
      reorderItems,
      moveItem,
      toggleItemVisibility,
      openDrawer,
      closeDrawer,
      toggleDrawer,
      enableEditMode,
      disableEditMode,
      toggleEditMode,
      setDockHover,
      setDockDimensions,
      triggerAction,
    ]
  );

  return (
    <DockContext.Provider value={contextValue}>{children}</DockContext.Provider>
  );
}

/**
 * Hook to access dock context
 * @throws Error if used outside of DockProvider
 */
export function useDock(): DockContextValue {
  const context = useContext(DockContext);

  if (!context) {
    throw new Error('useDock must be used within a DockProvider');
  }

  return context;
}

/**
 * Hook to get only visible items (for main dock display)
 */
export function useVisibleDockItems(): DockItem[] {
  const { items } = useDock();
  return useMemo(
    () =>
      items
        .filter((item) => item.isVisible)
        .sort((a, b) => a.position - b.position),
    [items]
  );
}

/**
 * Hook to get all items sorted by position (for drawer display)
 */
export function useAllDockItems(): DockItem[] {
  const { items } = useDock();
  return useMemo(
    () => [...items].sort((a, b) => a.position - b.position),
    [items]
  );
}
